/**
 * CoordinateTransformPanel - Coordinate transformation tool UI
 *
 * Provides source/target CRS selectors, coordinate input/output fields,
 * batch transformation support, copy functionality, and accuracy info.
 *
 * Validates: Requirements 46.1, 46.2, 46.3, 46.4, 46.5
 */
import React, { useState, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  IconButton,
  Tabs,
  Tab,
  Tooltip,
  Snackbar,
  Divider,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TransformIcon from '@mui/icons-material/Transform';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
  closeTransformTool,
  setTransformSourceCRS,
  setTransformTargetCRS,
  setTransformSourceCoordinates,
  setTransformTargetCoordinates,
  setTransformBatchInput,
  setTransformBatchOutput,
  setAccuracyInfo,
} from '../store/slices/coordinateSystemSlice';
import {
  getCommonCRS,
  transformCoordinates,
  batchTransformCoordinates,
  getTransformAccuracyInfo,
  formatCoordinates,
  type CRSInfo,
} from '../services/CoordinateSystemManager';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const CoordinateTransformPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const { transformToolOpen, transform, accuracyInfo } = useAppSelector(
    (state) => state.coordinateSystem
  );

  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [copySnackbar, setCopySnackbar] = useState(false);

  const commonCRS: CRSInfo[] = useMemo(() => getCommonCRS(), []);

  const handleClose = () => {
    dispatch(closeTransformTool());
    setError(null);
  };

  const handleSwapCRS = () => {
    const currentSource = transform.sourceCRS;
    const currentTarget = transform.targetCRS;
    dispatch(setTransformSourceCRS(currentTarget));
    dispatch(setTransformTargetCRS(currentSource));
    // Clear results when swapping
    dispatch(setTransformTargetCoordinates(''));
    dispatch(setTransformBatchOutput(''));
  };

  // Parse coordinate string "x, y" or "x y"
  const parseCoordinate = (input: string): [number, number] | null => {
    const trimmed = input.trim();
    // Try comma-separated
    let parts = trimmed.split(',').map((s) => s.trim());
    if (parts.length !== 2) {
      // Try space-separated
      parts = trimmed.split(/\s+/);
    }
    if (parts.length !== 2) return null;

    const x = parseFloat(parts[0]);
    const y = parseFloat(parts[1]);
    if (isNaN(x) || isNaN(y)) return null;
    return [x, y];
  };

  // Single coordinate transformation
  const handleTransformSingle = useCallback(() => {
    setError(null);
    const coord = parseCoordinate(transform.sourceCoordinates);
    if (!coord) {
      setError('Invalid coordinate format. Use "x, y" or "x y" format.');
      return;
    }

    try {
      const result = transformCoordinates(coord, transform.sourceCRS, transform.targetCRS);
      const formatted = formatCoordinates(result, transform.targetCRS);
      dispatch(setTransformTargetCoordinates(formatted));

      // Update accuracy info
      const accuracy = getTransformAccuracyInfo(transform.sourceCRS, transform.targetCRS);
      dispatch(setAccuracyInfo(accuracy));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Transformation failed');
    }
  }, [dispatch, transform.sourceCoordinates, transform.sourceCRS, transform.targetCRS]);

  // Batch coordinate transformation
  const handleTransformBatch = useCallback(() => {
    setError(null);
    const lines = transform.batchInput
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (lines.length === 0) {
      setError('No coordinates to transform. Enter one coordinate pair per line.');
      return;
    }

    const coords: [number, number][] = [];
    for (let i = 0; i < lines.length; i++) {
      const coord = parseCoordinate(lines[i]);
      if (!coord) {
        setError(`Invalid coordinate at line ${i + 1}: "${lines[i]}"`);
        return;
      }
      coords.push(coord);
    }

    try {
      const results = batchTransformCoordinates(coords, transform.sourceCRS, transform.targetCRS);
      const output = results.map((r) => formatCoordinates(r, transform.targetCRS)).join('\n');
      dispatch(setTransformBatchOutput(output));

      // Update accuracy info
      const accuracy = getTransformAccuracyInfo(transform.sourceCRS, transform.targetCRS);
      dispatch(setAccuracyInfo(accuracy));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Batch transformation failed');
    }
  }, [dispatch, transform.batchInput, transform.sourceCRS, transform.targetCRS]);

  // Copy to clipboard
  const handleCopy = useCallback((text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopySnackbar(true);
    });
  }, []);

  return (
    <>
      <Dialog
        open={transformToolOpen}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        data-testid="coordinate-transform-dialog"
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TransformIcon color="primary" />
            <Typography variant="h6">Coordinate Transformation</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {/* CRS Selectors */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel id="source-crs-label">Source CRS</InputLabel>
              <Select
                labelId="source-crs-label"
                value={transform.sourceCRS}
                label="Source CRS"
                onChange={(e) => dispatch(setTransformSourceCRS(e.target.value))}
                size="small"
                data-testid="transform-source-crs"
              >
                {commonCRS.map((crs) => (
                  <MenuItem key={crs.epsgCode} value={crs.epsgCode}>
                    {crs.epsgCode} - {crs.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Tooltip title="Swap source and target CRS">
              <IconButton onClick={handleSwapCRS} color="primary" aria-label="swap CRS">
                <SwapHorizIcon />
              </IconButton>
            </Tooltip>

            <FormControl sx={{ flex: 1 }}>
              <InputLabel id="target-crs-label">Target CRS</InputLabel>
              <Select
                labelId="target-crs-label"
                value={transform.targetCRS}
                label="Target CRS"
                onChange={(e) => dispatch(setTransformTargetCRS(e.target.value))}
                size="small"
                data-testid="transform-target-crs"
              >
                {commonCRS.map((crs) => (
                  <MenuItem key={crs.epsgCode} value={crs.epsgCode}>
                    {crs.epsgCode} - {crs.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Tabs for single/batch */}
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Single Coordinate" />
            <Tab label="Batch Transform" />
          </Tabs>

          {/* Single coordinate tab */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Source Coordinates (x, y)"
                placeholder="e.g. 12.4924, 41.8902"
                value={transform.sourceCoordinates}
                onChange={(e) => dispatch(setTransformSourceCoordinates(e.target.value))}
                fullWidth
                size="small"
                data-testid="source-coordinates-input"
              />

              <Button
                variant="contained"
                onClick={handleTransformSingle}
                startIcon={<TransformIcon />}
                disabled={!transform.sourceCoordinates.trim()}
              >
                Transform
              </Button>

              {transform.targetCoordinates && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="body1"
                    fontFamily="monospace"
                    sx={{ flex: 1 }}
                    data-testid="target-coordinates-output"
                  >
                    {transform.targetCoordinates}
                  </Typography>
                  <Tooltip title="Copy to clipboard">
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(transform.targetCoordinates)}
                      aria-label="copy coordinates"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </TabPanel>

          {/* Batch transform tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Source Coordinates (one per line)"
                placeholder={'12.4924, 41.8902\n2.3522, 48.8566\n-0.1276, 51.5074'}
                value={transform.batchInput}
                onChange={(e) => dispatch(setTransformBatchInput(e.target.value))}
                fullWidth
                multiline
                rows={5}
                size="small"
                data-testid="batch-input"
              />

              <Button
                variant="contained"
                onClick={handleTransformBatch}
                startIcon={<TransformIcon />}
                disabled={!transform.batchInput.trim()}
              >
                Transform All
              </Button>

              {transform.batchOutput && (
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Transformed Coordinates
                    </Typography>
                    <Tooltip title="Copy all to clipboard">
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(transform.batchOutput)}
                        aria-label="copy all coordinates"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <TextField
                    value={transform.batchOutput}
                    fullWidth
                    multiline
                    rows={5}
                    size="small"
                    slotProps={{ input: { readOnly: true } }}
                    sx={{ fontFamily: 'monospace' }}
                    data-testid="batch-output"
                  />
                </Box>
              )}
            </Box>
          </TabPanel>

          {/* Error display */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {/* Accuracy info */}
          {accuracyInfo && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <InfoOutlinedIcon fontSize="small" color="info" />
                <Typography variant="subtitle2" color="text.secondary">
                  Transformation Accuracy
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip label="Accuracy" size="small" variant="outlined" />
                  <Typography variant="body2">{accuracyInfo.estimatedAccuracy}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip label="Area" size="small" variant="outlined" />
                  <Typography variant="body2">{accuracyInfo.applicableArea}</Typography>
                </Box>
                {accuracyInfo.notes && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    {accuracyInfo.notes}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Copy success snackbar */}
      <Snackbar
        open={copySnackbar}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar(false)}
        message="Coordinates copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default CoordinateTransformPanel;
